import * as React from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWriteJson, useWriteBytes } from "../hooks";
import { useFlowFSStore } from "../state/flowfs-store";
import { useShallow } from "zustand/react/shallow";
import { toast } from "sonner";

export default function UploadZone({ tenantId, flowId }: { tenantId: string; flowId: string }) {
  const prefix = useFlowFSStore(useShallow((s) => s.prefix));
  const writeJson = useWriteJson(tenantId, flowId);
  const writeBytes = useWriteBytes(tenantId, flowId);
  const [as, setAs] = React.useState<"json" | "text" | "binary">("json");
  const [sensitive, setSensitive] = React.useState(false);

  const onDrop = React.useCallback(
    async (accepted: File[]) => {
      for (const f of accepted) {
        const dst = (prefix.endsWith("/") ? prefix : prefix + "/") + f.name;
        try {
          if (as === "json" || as === "text") {
            const txt = await f.text();
            const data = as === "json" ? JSON.parse(txt) : txt;
            await writeJson.mutateAsync({
              path: dst,
              data,
              as: as as any,
              createOnly: true,
              sensitive,
              contentType: as === "json" ? "application/json" : "text/plain",
            });
          } else {
            await writeBytes.mutateAsync({
              path: dst,
              bytes: f,
              contentType: f.type || "application/octet-stream",
              createOnly: true,
            });
          }
        } catch (e: any) {
          toast.error(e?.message || `Upload failed: ${f.name}`);
        }
      }
    },
    [prefix, as, sensitive, writeJson, writeBytes]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  return (
    <div className="border-b p-2">
      <div className="flex items-center gap-2">
        <div {...getRootProps({ className: "flex-1 border rounded p-3 text-sm cursor-pointer" })}>
          <input {...getInputProps()} />
          {isDragActive ? "Drop files here…" : "Drag & drop files here, or click to select"}
        </div>
        <div className="grid gap-1 w-[180px]">
          <Label className="text-xs">Mode</Label>
          <select className="border rounded h-9 px-2" value={as} onChange={(e) => setAs(e.target.value as any)}>
            <option value="json">JSON</option>
            <option value="text">Text</option>
            <option value="binary">Binary</option>
          </select>
        </div>
        <div className="grid gap-1 w-[140px]">
          <Label className="text-xs">Sensitive</Label>
          <label className="flex items-center gap-2 text-sm">
            <Input type="checkbox" checked={sensitive} onChange={(e) => setSensitive(e.target.checked)} />
            <span className="text-xs">Mark</span>
          </label>
        </div>
        <Button onClick={() => (document.querySelector<HTMLInputElement>('input[type="file"]')?.click())}>Upload</Button>
      </div>
    </div>
  );
}
